import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Spinner, Badge } from 'reactstrap';
import { format } from 'date-fns';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BASE_URL } from '../constants/apiConfig';
import AssignReviewerModal from './AssignReviewerModal';
import './ViewApplication.css';

const ViewApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [files, setFiles] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [assignReviewerModal, setAssignReviewerModal] = useState(false);

  const fetchApplicationData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/applications/${id}/full`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setData(response.data);
    } catch (error) {
      console.error('Error fetching application:', error);
      toast.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationFiles = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/applications/${id}/files`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  useEffect(() => {
    fetchApplicationData();
    fetchApplicationFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/applications/${id}/download-zip`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Application_${id}_Files.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Files downloaded successfully');
    } catch (error) {
      console.error('Error downloading zip:', error);
      toast.error('Failed to download files');
    } finally {
      setDownloadingZip(false);
    }
  };

  const renderImage = (imageUrl) => {
    if (!imageUrl) return 'Not uploaded';
    return (
      <img
        src={imageUrl}
        alt="Profile"
        style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', borderRadius: '8px' }}
      />
    );
  };

  const renderFileLink = (file, label) => {
    if (!file) {
      return <span className="text-muted">Not uploaded</span>;
    }

    return (
      <div className="d-flex align-items-center gap-2">
        <a
          href={file}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary text-decoration-none"
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
          </svg>
          View {label}
        </a>
        <span className="text-muted">|</span>
        <a
          href={file}
          download
          target="_blank"
          rel="noopener noreferrer"
          className="text-success text-decoration-none"
          style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" style={{ flexShrink: 0 }}>
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
          </svg>
          Download
        </a>
      </div>
    );
  };

  const getStatusBadgeColor = (status) => {
    const statusColors = {
      Draft: 'warning',
      Submitted: 'info',
      'Review 1 - In Progress': 'primary',
      'Review 1 - Completed': 'success',
      'Review 2 - In Progress': 'primary',
      'Review 2 - Completed': 'success',
      Shortlisted: 'success',
      Rejected: 'danger',
    };
    return statusColors[status] || 'secondary';
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3">Loading application...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-5">
        <h4>Application not found</h4>
        <Button color="primary" onClick={() => navigate('/admin/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const { personalInfo, educationalBackground, professionalExperience, recommendations, fellowshipInformation, additionalInformation, applicationMeta } = data;

  return (
    <div className="container">
      {/* Header Section */}
      <div className="application-header">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h1 className="application-title">Application Details</h1>
            <p className="text-muted">Application ID: {applicationMeta.id}</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <Badge color={getStatusBadgeColor(applicationMeta.status)} className="status-badge">
              {applicationMeta.status}
            </Badge>
          </div>
        </div>

        <div className="action-buttons d-flex gap-2 flex-wrap mb-4">
          <Button color="secondary" outline onClick={() => navigate('/admin/dashboard')}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Dashboard
          </Button>
          <Button
            color="success"
            onClick={handleDownloadZip}
            disabled={downloadingZip || files.length === 0}
          >
            {downloadingZip ? (
              <>
                <Spinner size="sm" className="me-2" />
                Downloading...
              </>
            ) : (
              <>
                <i className="bi bi-file-earmark-zip me-2"></i>
                Download All Files
              </>
            )}
          </Button>
          <Button color="primary" onClick={() => setAssignReviewerModal(true)}>
            <i className="bi bi-person-plus me-2"></i>
            Assign Reviewer
          </Button>
        </div>
      </div>

      {/* Personal Information Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-person-circle"></i>
          </div>
          <h3 className="section-title">Personal Information</h3>
        </div>
        <div className="preview-section-content">
          {personalInfo?.personalInfo && (
            <>
              {personalInfo.image && (
                <div className="text-center mb-4">
                  <div className="profile-image-wrapper">{renderImage(personalInfo.image)}</div>
                </div>
              )}
              <Row>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Full Name</label>
                    <div className="preview-value">
                      {personalInfo.personalInfo.firstName} {personalInfo.personalInfo.middleName} {personalInfo.personalInfo.lastName}
                    </div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Date of Birth</label>
                    <div className="preview-value">
                      {personalInfo.personalInfo.dateOfBirth
                        ? format(new Date(personalInfo.personalInfo.dateOfBirth), 'dd MMMM yyyy')
                        : 'Not provided'}
                    </div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Gender</label>
                    <div className="preview-value">{personalInfo.personalInfo.gender || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Nationality</label>
                    <div className="preview-value">{personalInfo.personalInfo.nationality || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Email Address</label>
                    <div className="preview-value">
                      <a href={`mailto:${personalInfo.personalInfo.email}`}>{personalInfo.personalInfo.email || 'Not provided'}</a>
                    </div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Phone Number</label>
                    <div className="preview-value">{personalInfo.personalInfo.phone || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Mailing Address</label>
                    <div className="preview-value">
                      {personalInfo.personalInfo.mailingAddress?.address && (
                        <>
                          {personalInfo.personalInfo.mailingAddress.address}
                          <br />
                          {personalInfo.personalInfo.mailingAddress.city}, {personalInfo.personalInfo.mailingAddress.state}
                          <br />
                          {personalInfo.personalInfo.mailingAddress.country} - {personalInfo.personalInfo.mailingAddress.zipCode}
                        </>
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Permanent Address</label>
                    <div className="preview-value">
                      {personalInfo.personalInfo.useMailingAsPermanent ? (
                        <span className="badge bg-info">Same as Mailing Address</span>
                      ) : (
                        personalInfo.personalInfo.permanentAddress?.address && (
                          <>
                            {personalInfo.personalInfo.permanentAddress.address}
                            <br />
                            {personalInfo.personalInfo.permanentAddress.city}, {personalInfo.personalInfo.permanentAddress.state}
                            <br />
                            {personalInfo.personalInfo.permanentAddress.country} - {personalInfo.personalInfo.permanentAddress.zipCode}
                          </>
                        )
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">CV/Resume</label>
                    <div className="preview-value">
                      <div className="file-link">{renderFileLink(personalInfo.document, 'CV/Resume')}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </div>
      </div>

      {/* Educational Background Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-mortarboard"></i>
          </div>
          <h3 className="section-title">Educational Background</h3>
        </div>
        <div className="preview-section-content">
          {educationalBackground && Object.keys(educationalBackground).length > 0 ? (
            <Row>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Degree</label>
                  <div className="preview-value">{educationalBackground.degree || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Field of Study</label>
                  <div className="preview-value">{educationalBackground.fieldOfStudy || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Graduation Year</label>
                  <div className="preview-value">{educationalBackground.graduationYear || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Institution</label>
                  <div className="preview-value">{educationalBackground.institutionName || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="12" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Location</label>
                  <div className="preview-value">
                    {educationalBackground.city}, {educationalBackground.state}, {educationalBackground.country}
                  </div>
                </div>
              </Col>
              {educationalBackground.webpage && (
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Institution Webpage</label>
                    <div className="preview-value">
                      <a href={educationalBackground.webpage} target="_blank" rel="noopener noreferrer" className="external-link">
                        {educationalBackground.webpage}
                      </a>
                    </div>
                  </div>
                </Col>
              )}
              <Col md="12" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Certificate</label>
                  <div className="preview-value">
                    <div className="file-link">{renderFileLink(educationalBackground.file, 'Certificate')}</div>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <p className="no-data-message">No educational background information provided</p>
          )}
        </div>
      </div>

      {/* Professional Experience Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-briefcase"></i>
          </div>
          <h3 className="section-title">Professional Experience</h3>
        </div>
        <div className="preview-section-content">
          {professionalExperience && Object.keys(professionalExperience).length > 0 ? (
            <Row>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Job Title / Position</label>
                  <div className="preview-value">{professionalExperience.jobTitle || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Organization / Company</label>
                  <div className="preview-value">{professionalExperience.organisation_name || 'Not provided'}</div>
                </div>
              </Col>

              {professionalExperience.responsibilities && (
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Job Description</label>
                    <div className="preview-value description-text">{professionalExperience.responsibilities}</div>
                  </div>
                </Col>
              )}
            </Row>
          ) : (
            <p className="no-data-message">No professional experience information provided</p>
          )}
        </div>
      </div>

      {/* Recommendations Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-person-check"></i>
          </div>
          <h3 className="section-title">Recommendations</h3>
        </div>
        <div className="preview-section-content">
          {recommendations?.recommendation1 && Object.keys(recommendations.recommendation1).length > 0 ? (
            <div className="recommendation-card">
              <div className="recommendation-badge">Referee 1</div>
              <Row>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Name</label>
                    <div className="preview-value">{recommendations.recommendation1.name || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Email</label>
                    <div className="preview-value">
                      {recommendations.recommendation1.email ? (
                        <a href={`mailto:${recommendations.recommendation1.email}`}>{recommendations.recommendation1.email}</a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Phone</label>
                    <div className="preview-value">{recommendations.recommendation1.mobile || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Designation</label>
                    <div className="preview-value">{recommendations.recommendation1.designation || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Institution</label>
                    <div className="preview-value">{recommendations.recommendation1.institution || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Institution Webpage</label>
                    <div className="preview-value">
                      {recommendations.recommendation1.institution_webpage ? (
                        <a
                          href={recommendations.recommendation1.institution_webpage}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {recommendations.recommendation1.institution_webpage}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Recommendation Letter</label>
                    <div className="preview-value">
                      <div className="file-link">{renderFileLink(recommendations.recommendation1.file, 'Letter')}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <p className="no-data-message">No recommendation 1 information provided</p>
          )}

          {recommendations?.recommendation2 && Object.keys(recommendations.recommendation2).length > 0 && (
            <div className="recommendation-card mt-4">
              <div className="recommendation-badge">Referee 2</div>
              <Row>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Name</label>
                    <div className="preview-value">{recommendations.recommendation2.name || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Email</label>
                    <div className="preview-value">
                      {recommendations.recommendation2.email ? (
                        <a href={`mailto:${recommendations.recommendation2.email}`}>{recommendations.recommendation2.email}</a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Phone</label>
                    <div className="preview-value">{recommendations.recommendation2.mobile || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Designation</label>
                    <div className="preview-value">{recommendations.recommendation2.designation || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Institution</label>
                    <div className="preview-value">{recommendations.recommendation2.institution || 'Not provided'}</div>
                  </div>
                </Col>
                <Col md="6" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Institution Webpage</label>
                    <div className="preview-value">
                      {recommendations.recommendation2.institution_webpage ? (
                        <a
                          href={recommendations.recommendation2.institution_webpage}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="external-link"
                        >
                          {recommendations.recommendation2.institution_webpage}
                        </a>
                      ) : (
                        'Not provided'
                      )}
                    </div>
                  </div>
                </Col>
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Recommendation Letter</label>
                    <div className="preview-value">
                      <div className="file-link">{renderFileLink(recommendations.recommendation2.file, 'Letter')}</div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>

      {/* Fellowship Information Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-award"></i>
          </div>
          <h3 className="section-title">Fellowship Information</h3>
        </div>
        <div className="preview-section-content">
          {fellowshipInformation && Object.keys(fellowshipInformation).length > 0 ? (
            <Row>
              <Col md="12" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Program Applied For</label>
                  <div className="preview-value highlight-value">{fellowshipInformation.programAppliedFor || 'Not provided'}</div>
                </div>
              </Col>
              <Col md="12" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Selected Themes</label>
                  <div className="preview-value">
                    {fellowshipInformation.themes && Object.keys(fellowshipInformation.themes).length > 0 ? (
                      <div className="themes-badges">
                        {Object.entries(fellowshipInformation.themes)
                          .filter(([key, value]) => value === true)
                          .map(([key]) => (
                            <span key={key} className="theme-badge">
                              {key}
                            </span>
                          ))}
                      </div>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                </div>
              </Col>
              {fellowshipInformation.othersField && (
                <Col md="12" className="mb-3">
                  <div className="preview-field">
                    <label className="preview-label">Other Theme Specification</label>
                    <div className="preview-value">{fellowshipInformation.othersField}</div>
                  </div>
                </Col>
              )}
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Book Proposal</label>
                  <div className="preview-value">
                    <div className="file-link">{renderFileLink(fellowshipInformation.bookProposal, 'Book Proposal')}</div>
                  </div>
                </div>
              </Col>
              <Col md="6" className="mb-3">
                <div className="preview-field">
                  <label className="preview-label">Writing Sample</label>
                  <div className="preview-value">
                    <div className="file-link">{renderFileLink(fellowshipInformation.writingSample, 'Writing Sample')}</div>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <p className="no-data-message">No fellowship information provided</p>
          )}
        </div>
      </div>

      {/* Additional Information Section */}
      <div className="preview-section-card">
        <div className="preview-section-header">
          <div className="section-icon">
            <i className="bi bi-file-earmark-text"></i>
          </div>
          <h3 className="section-title">Additional Information</h3>
        </div>
        <div className="preview-section-content">
          {additionalInformation && Object.keys(additionalInformation).length > 0 ? (
            <>
              <div className="mb-4">
                <h5 className="subsection-title">Publications</h5>
                {additionalInformation.publications && additionalInformation.publications.length > 0 && additionalInformation.publications.some((pub) => pub.name && pub.name.trim()) ? (
                  <div className="publications-list">
                    {additionalInformation.publications.map(
                      (pub, index) =>
                        pub.name && (
                          <div key={index} className="publication-item">
                            <div className="publication-number">{index + 1}</div>
                            <div className="publication-details">
                              <div className="preview-field">
                                <label className="preview-label">Title</label>
                                <div className="preview-value">{pub.name}</div>
                              </div>
                              {pub.url && (
                                <div className="preview-field mt-2">
                                  <label className="preview-label">URL</label>
                                  <div className="preview-value">
                                    <a href={pub.url} target="_blank" rel="noopener noreferrer" className="external-link">
                                      {pub.url}
                                    </a>
                                  </div>
                                </div>
                              )}
                              {pub.file && (
                                <div className="preview-field mt-2">
                                  <label className="preview-label">File</label>
                                  <div className="preview-value">
                                    <div className="file-link">{renderFileLink(pub.file, `Publication ${index + 1}`)}</div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                    )}
                  </div>
                ) : (
                  <div className="preview-value">
                    <span className="social-platform">NA</span>
                  </div>
                )}
              </div>

              {additionalInformation.socialMedia && additionalInformation.socialMedia.length > 0 && (
                <div className="mb-4">
                  <h5 className="subsection-title">Social Media Profiles</h5>
                  <div className="social-media-list">
                    {additionalInformation.socialMedia.map(
                      (social, index) =>
                        social.platform && (
                          <div key={index} className="social-media-item">
                            {social.platform === 'NA' ? (
                              <span className="social-platform">NA</span>
                            ) : (
                              <>
                                <span className="social-platform">{social.platform}:</span>
                                <a href={social.url} target="_blank" rel="noopener noreferrer" className="social-link">
                                  {social.url}
                                </a>
                              </>
                            )}
                          </div>
                        )
                    )}
                  </div>
                </div>
              )}

              <div className="terms-section">
                <label className="preview-label">Terms & Conditions</label>
                <div className="preview-value">
                  {additionalInformation.termsAccepted ? (
                    <span className="badge bg-success">
                      <i className="bi bi-check-circle-fill me-2"></i>
                      Accepted
                    </span>
                  ) : (
                    <span className="badge bg-danger">
                      <i className="bi bi-x-circle-fill me-2"></i>
                      Not Accepted
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <p className="no-data-message">No additional information provided</p>
          )}
        </div>
      </div>

      {/* Assign Reviewer Modal */}
      <AssignReviewerModal
        isOpen={assignReviewerModal}
        toggle={() => setAssignReviewerModal(!assignReviewerModal)}
        applicationId={id}
        onSuccess={() => {
          fetchApplicationData();
          setAssignReviewerModal(false);
        }}
      />
    </div>
  );
};

export default ViewApplication;
